import { shuffle } from "lodash";
import Group from "../groups/Group";
import { SortType } from "../settings/model/SortType";
import Term from "./Term";
import { TermLevel } from "./custom_view/TermLevel";
import i18n from "i18next";
import { seededShuffle } from "../common/numbers";

export function getFilteredTermsWithQuery(
  query: string,
  fromTerms: Term[]
): Term[] {
  const lowerCaseQuery = query.toLowerCase();
  return fromTerms.filter(
    (term) =>
      term.term.toLowerCase().includes(lowerCaseQuery) ||
      term.definition.toLowerCase().includes(lowerCaseQuery) ||
      term.memo.toLowerCase().includes(lowerCaseQuery) ||
      term.pronunciation.toLowerCase().includes(lowerCaseQuery)
  );
}

/**
 * 상세 보기 / 예문은 화면의 로컬 상태에만 있으므로,
 * rawTerms 로 목록을 다시 만들 때 이전 목록의 값을 id 기준으로 이어 붙인다.
 */
export function keepLocalTermState(newTerms: Term[], prevTerms: Term[]): Term[] {
  const prevTermsById = new Map(prevTerms.map((t) => [t.id, t]));
  return newTerms.map((term) => {
    const prevTerm = prevTermsById.get(term.id);
    if (prevTerm === undefined) return term;
    return {
      ...term,
      isShowDetail: prevTerm.isShowDetail,
      sentence: prevTerm.sentence,
      isGeneratingSentence: prevTerm.isGeneratingSentence,
      isMoreSentenceLoading: prevTerm.isMoreSentenceLoading,
    };
  });
}

export function updateAndReturnTermMemo(
  terms: Term[],
  termId: string,
  memo: string
): Term[] {
  return terms.map((t) => (t.id === termId ? { ...t, memo } : t));
}

export function deleteAndReturnTerm(terms: Term[], term: Term): Term[] {
  const termIndex = terms.findIndex((t) => t.id === term.id);
  if (termIndex !== -1) {
    const newTerms = [...terms];
    newTerms.splice(termIndex, 1);
    return newTerms;
  } else {
    return terms;
  }
}

export function updateAndReturnTerm(terms: Term[], term: Term): Term[] {
  const termIndex = terms.findIndex((t) => t.id === term.id);
  if (termIndex !== -1) {
    const newTerms = [...terms];
    newTerms[termIndex] = term;
    return newTerms;
  } else {
    return terms;
  }
}

export function updateAndReturnTermMark(
  terms: Term[],
  term: Term,
  isOn: boolean
): Term[] {
  const termIndex = terms.findIndex((t) => t.id === term.id);
  if (termIndex !== -1) {
    const newTerms = [...terms];
    const updatedTerm = {
      ...newTerms[termIndex],
      isMarked: isOn,
    };
    newTerms[termIndex] = Term.fromJson(updatedTerm);
    return newTerms;
  } else {
    return terms;
  }
}

export function updateAndReturnTermLevel(
  terms: Term[],
  term: Term,
  toLevel: TermLevel,
  toExp: number
): Term[] {
  const termIndex = terms.findIndex((t) => t.id === term.id);
  if (termIndex !== -1) {
    const newTerms = [...terms];
    const updatedTerm = {
      ...newTerms[termIndex],
      level: toLevel,
      exp: toExp,
    };
    newTerms[termIndex] = Term.fromJson(updatedTerm);
    return newTerms;
  } else {
    return terms;
  }
}

export function duplicatedTermsSorted(terms: Term[]): Term[] {
  if (!terms || terms.length === 0) return [];

  // 1) term별 등장 횟수 계산
  const counts = new Map<string, number>();
  for (const t of terms) {
    const key = t.term ?? "";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  // 2) 2회 이상 등장하는 term만 추출
  const duplicatedKeys = new Set(
    Array.from(counts.entries())
      .filter(([_, count]) => count > 1)
      .map(([term]) => term)
  );

  // 3) 원본에서 중복 term만 남기기
  const filtered = terms.filter((t) => duplicatedKeys.has(t.term));

  // 4) term → group 순으로 정렬
  filtered.sort((a, b) => {
    const termCompare = a.term.localeCompare(b.term);
    if (termCompare !== 0) return termCompare;
    return a.group.localeCompare(b.group);
  });

  return filtered;
}

export function getFilteredTermsWithOptions(
  terms: Term[],
  seed?: number,
  sortType?: SortType,
  selectedGroups?: Group[],
  selectedLevels?: TermLevel[],
  marked?: boolean
): Term[] {
  let filteredTerms: Term[] = [];
  if (selectedGroups !== undefined && selectedGroups.length !== 0) {
    filteredTerms = selectedGroups.reduce<Term[]>((acc, group) => {
      if (group.isAllGroup) {
        return terms;
      }

      const groupTerms = terms.filter((term) => {
        if (group.isUnspecifiedGroup) {
          return term.group === "";
        }
        return term.group === group.title;
      });

      return acc.concat(groupTerms);
    }, []);
  } else {
    filteredTerms = [...terms];
  }

  if (selectedLevels !== undefined) {
    filteredTerms = filteredTerms.filter((term) =>
      selectedLevels.includes(term.level)
    );
  }

  if (marked !== undefined) {
    filteredTerms = filteredTerms.filter((term) => term.isMarked === marked);
  }

  if (sortType !== undefined) {
    switch (sortType) {
      case SortType.recent:
        break;
      case SortType.old:
        filteredTerms.reverse();
        break;
      case SortType.abc:
        filteredTerms.sort((a, b) => a.term.localeCompare(b.term));
        break;
      case SortType.random:
        let shuffledTerms: Term[] = [];
        if (seed !== undefined) {
          const sortedTerms = [...filteredTerms].sort((a, b) =>
            a.id.localeCompare(b.id)
          );
          shuffledTerms = seededShuffle(sortedTerms, seed);
        } else {
          shuffledTerms = shuffle(filteredTerms);
        }
        filteredTerms = shuffledTerms;
        break;
    }
  }

  return filteredTerms;
}

export function convertSortTypeString(sortType: SortType): string {
  switch (sortType) {
    case SortType.recent:
      return "asc";
    case SortType.old:
      return "desc";
    case SortType.abc:
      return "abc";
    case SortType.random:
      return "random";
  }
}

export function makeSortTypeString(sortType: SortType): string {
  switch (sortType) {
    case SortType.recent:
      return i18n.t("Latest");
    case SortType.old:
      return i18n.t("Old");
    case SortType.abc:
      return i18n.t("Alphabet");
    case SortType.random:
      return i18n.t("Random");
  }
}

export function isIncludeLevel(level: TermLevel, from: TermLevel[]): boolean {
  return from.includes(level);
}

export function toggleTermLevel(
  checked: boolean,
  level: TermLevel,
  from: TermLevel[]
): TermLevel[] {
  if (checked) {
    if (!from.includes(level)) {
      return [...from, level];
    }
  } else {
    if (from.includes(level)) {
      return from.filter((item) => item !== level);
    }
  }
  return from;
}

export function makeSelectedLevelsString(
  selectedLevels: TermLevel[],
  style?: "learning"
): string {
  if (style === "learning") {
    const levels = [];
    if (selectedLevels.includes(TermLevel.difficult)) {
      levels.push(i18n.t("Difficult"));
    }

    if (selectedLevels.includes(TermLevel.familiar)) {
      levels.push(i18n.t("Familiar"));
    }

    if (selectedLevels.includes(TermLevel.perfect)) {
      levels.push(i18n.t("Perfect"));
    }

    const resultString = levels.join(", ");
    return resultString;
  } else {
    if (
      selectedLevels.includes(TermLevel.difficult) === true &&
      selectedLevels.includes(TermLevel.familiar) === true &&
      selectedLevels.includes(TermLevel.perfect) === true
    ) {
      return `${i18n.t("Difficult")}, ${i18n.t("Familiar")}, ${i18n.t(
        "Perfect"
      )}`;
    } else if (
      selectedLevels.includes(TermLevel.difficult) === true &&
      selectedLevels.includes(TermLevel.familiar) === true &&
      selectedLevels.includes(TermLevel.perfect) === false
    ) {
      return `${i18n.t("Difficult")}, ${i18n.t("Familiar")}`;
    } else if (
      selectedLevels.includes(TermLevel.difficult) === false &&
      selectedLevels.includes(TermLevel.familiar) === false &&
      selectedLevels.includes(TermLevel.perfect) === true
    ) {
      return i18n.t("Perfect");
    } else if (
      selectedLevels.includes(TermLevel.difficult) === true &&
      selectedLevels.includes(TermLevel.familiar) === false &&
      selectedLevels.includes(TermLevel.perfect) === false
    ) {
      return i18n.t("Difficult");
    } else if (
      selectedLevels.includes(TermLevel.difficult) === false &&
      selectedLevels.includes(TermLevel.familiar) === true &&
      selectedLevels.includes(TermLevel.perfect) === false
    ) {
      return i18n.t("Familiar");
    } else if (
      selectedLevels.includes(TermLevel.difficult) === false &&
      selectedLevels.includes(TermLevel.familiar) === true &&
      selectedLevels.includes(TermLevel.perfect) === true
    ) {
      return `${i18n.t("Familiar")}, ${i18n.t("Perfect")}`;
    } else if (
      selectedLevels.includes(TermLevel.difficult) === true &&
      selectedLevels.includes(TermLevel.familiar) === false &&
      selectedLevels.includes(TermLevel.perfect) === true
    ) {
      return `${i18n.t("Difficult")}, ${i18n.t("Perfect")}`;
    } else {
      return "Wrong value.";
    }
  }
}
